/**
 * The order-list pages' client-side `Export CSV` — built
 * from rows already on screen, never a server round trip.
 *
 * 🔀 **ADDED 2026-09-28 for My Orders and Budget Cost Codes** (owner, on being
 * asked whether those two pages should also get `Fit columns to screen`:
 * *"add it in and a export and print options like the other pages have"*).
 *
 * 🚨 **WHY THIS IS NOT `handleExport` (Dept Orders / To Approve's CSV).**
 * Those two pages call `GET /api/orders/export?ids=…` — a SERVER route. My
 * Orders and Budget Cost Codes were asked for export/print "like the other
 * pages have" without a ruling to widen that server route's authority model
 * to a third and fourth caller, and a client-side export of rows the viewer
 * is ALREADY being shown discloses nothing new (the same argument
 * `OrderListPrintMenuItem.js` already makes for the PDF/print rows on every
 * order page). Building a client exporter keeps that property; posting a new
 * set of ids to the server export route would not.
 *
 * 🔑 **THE NINE COLUMNS ARE THE SAME NINE `utils/orderListPdf.js` PRINTS**,
 * not the twelve the TABLE renders. The table's Item/Quantity/Unit
 * Price/Total Price are a scrollable PREVIEW of potentially several line
 * items — there is no single value to put in one CSV cell per order for any
 * of the four. `orderListPdf.js`'s `ORDER_LIST_COLUMNS` already answered this
 * exact question for "Print all" / "Download list (PDF)" (one row per ORDER,
 * an item COUNT rather than a breakdown), so the CSV and PDF agree on what
 * "the list" means rather than each page picking its own shape.
 *
 * 🔑 THE FILE FORMAT MIRRORS `server/routes/orders.js`'s `/export` handler
 * and `utils/hireListExport.js`, byte for byte in SHAPE: a UTF-8 BOM, every
 * field double-quoted through the SAME `csvEscape` (formula-injection guard
 * included, via `rowsToCsv` from `gagCsvExport.js` — never a third copy),
 * `\r\n` line endings, a trailing newline. **There is no separate "Excel"
 * file** (owner 2026-10-28): it was this same text saved as `.xls`, which
 * Excel warns about on open — it is the one `.csv` now (`CSV_EXT` /
 * `CSV_MIME`), and Excel opens it (the BOM makes it read UTF-8).
 *
 * 🚨 **NO ACCOUNTING FIELDS.** `cost_code` / `payment_type` / `budget_pot_*`
 * are gated per order by `canViewOrderAccounting` and are never part of a
 * list ROW to begin with (`preview_items`'s allowlist) — this file reads
 * only order-level fields the list page already renders, so there is nothing
 * to leak.
 */
import { rowsToCsv, downloadTextFile, CSV_EXT, CSV_MIME } from './gagCsvExport';
import { toLocalISO, formatDate } from './dateFormat';
import { formatCurrency } from './formatCurrency';
import { EM_DASH } from './moneyOrAbsent';
import { getOrderNumber } from './orderTitle';
import { poDisplayText } from './orderPdfMeta';
import { cap } from './listPdf';
import { STATUS_LABELS } from '../constants/colors';

export const ORDER_EXPORT_HEADERS = Object.freeze([
  'PO Number', 'Order Number', 'Order Date', 'Needed By', 'Ordered By',
  'Job Name', 'Supplier', 'Items', 'Order Total', 'Status',
]);

/**
 * Mirrors `orderListPdf.js#itemCount` — a COUNT, matching the printed list.
 * 🪤 `item_count` is a `COUNT(*)` and so a STRING on PostgreSQL — `Number()`
 * before `Number.isFinite`, never `Number.isFinite` on the raw value.
 */
function itemCount(order) {
  const declared = order && order.item_count != null ? Number(order.item_count) : null;
  if (Number.isFinite(declared)) return declared;
  if (Array.isArray(order && order.items)) return order.items.length;
  if (Array.isArray(order && order.preview_items)) return order.preview_items.length;
  return order && order.first_item_description ? 1 : 0;
}

/** Mirrors `orderListPdf.js#statusText`. */
function statusText(order) {
  const raw = order && order.status;
  if (raw == null || String(raw).trim() === '') return '';
  const key = String(raw).trim();
  return STATUS_LABELS[key] || cap(key.replace(/_/g, ' '));
}

/** Mirrors `orderListPdf.js#totalText` — a derived field; a missing one is a dash, never £0.00. */
function totalText(order) {
  const raw = order && order.order_total;
  const value = raw == null ? null : Number(raw);
  const legacyRaw = order && order.total_amount;
  const legacy = value == null && legacyRaw != null ? Number(legacyRaw) : null;
  const amount = Number.isFinite(value) ? value : (Number.isFinite(legacy) ? legacy : null);
  return amount == null ? EM_DASH : formatCurrency(amount);
}

function orderRow(order) {
  return [
    poDisplayText(order) || null,
    getOrderNumber(order) || null,
    order.order_date ? formatDate(order.order_date) : null,
    order.required_by_date ? formatDate(order.required_by_date) : null,
    order.requested_by,
    order.job_rig,
    order.supplier_name,
    itemCount(order),
    totalText(order),
    statusText(order),
  ];
}

/** The whole CSV text for these orders. An empty list is the header row alone. */
export function ordersToCsv(orders) {
  return rowsToCsv(ORDER_EXPORT_HEADERS, (orders || []).map(orderRow));
}

/** `<slug>-<YYYY-MM-DD>.csv` — the orders/hires export filename shape. */
export function orderExportFilename(label, now = new Date()) {
  const slug = String(label || 'orders').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'orders';
  return `${slug}-${toLocalISO(now)}${CSV_EXT}`;
}

/** Build and save the export — always CSV. */
export function downloadOrderListExport(orders, label = 'orders') {
  downloadTextFile(ordersToCsv(orders), orderExportFilename(label), CSV_MIME);
}
